import { createSlice } from '@reduxjs/toolkit';
import {
  getOverallScores,
  getPerformanceAcrossTime,
  getResonanceRanking,
  getCreativeVsMedia,
} from '@/services/socialResonanceService';
import { logoutUser } from '@/store/slices/authSlice';
import {
  OverallScoresData,
  PerformanceHistoryResponse,
  ResonanceRankingYearData,
  CreativeVsMediaSeries,
  OverallScoresParams,
  PerformanceAcrossTimeParams,
  ResonanceRankingParams,
  CreativeVsMediaParams,
} from '@/types/socialResonance.types';
import {
  DataStateWithParams,
  createDataAsyncThunk,
  addDataAsyncCasesWithParams,
} from '@/store/utils/asyncThunkHelpers';

interface SocialResonanceState {
  socialResonance: DataStateWithParams<OverallScoresData, OverallScoresParams>;
  creativeDimensions: DataStateWithParams<PerformanceHistoryResponse, PerformanceAcrossTimeParams>;
  mediaMetrics: DataStateWithParams<ResonanceRankingYearData[], ResonanceRankingParams>;
  aiMeasurement: DataStateWithParams<CreativeVsMediaSeries[], CreativeVsMediaParams>;
}

const initialState: SocialResonanceState = {
  socialResonance: { data: null, status: 'idle', error: null },
  creativeDimensions: { data: null, status: 'idle', error: null },
  mediaMetrics: { data: null, status: 'idle', error: null },
  aiMeasurement: { data: null, status: 'idle', error: null },
};

export const fetchSocialResonance = createDataAsyncThunk<OverallScoresData, OverallScoresParams>(
  'socialResonance/fetchSocialResonance',
  getOverallScores
);

export const fetchCreativeDimensions = createDataAsyncThunk<
  PerformanceHistoryResponse,
  PerformanceAcrossTimeParams
>('socialResonance/fetchCreativeDimensions', getPerformanceAcrossTime);

export const fetchMediaMetrics = createDataAsyncThunk<
  ResonanceRankingYearData[],
  ResonanceRankingParams
>('socialResonance/fetchMediaMetrics', getResonanceRanking);

export const fetchAIMeasurement = createDataAsyncThunk<
  CreativeVsMediaSeries[],
  CreativeVsMediaParams
>('socialResonance/fetchAIMeasurement', getCreativeVsMedia);

const socialResonanceSlice = createSlice({
  name: 'socialResonance',
  initialState,
  reducers: {},
  extraReducers: builder => {
    addDataAsyncCasesWithParams(builder, fetchSocialResonance, state => state.socialResonance);
    addDataAsyncCasesWithParams(
      builder,
      fetchCreativeDimensions,
      state => state.creativeDimensions
    );
    addDataAsyncCasesWithParams(builder, fetchMediaMetrics, state => state.mediaMetrics);
    addDataAsyncCasesWithParams(builder, fetchAIMeasurement, state => state.aiMeasurement);

    // Reset state on logout
    builder.addCase(logoutUser.fulfilled, () => {
      return initialState;
    });
  },
});

export default socialResonanceSlice.reducer;
